import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

const FALLBACK_COLORS = [
  "#6366f1", "#8b5cf6", "#ec4899", "#f59e0b",
  "#10b981", "#06b6d4", "#3b82f6", "#ef4444",
];

const formatNumber = (n) => new Intl.NumberFormat("pt-BR").format(n);

const CausesWidget = ({ data = [], loading = false, totalClosed = 0 }) => {
  const [expanded, setExpanded] = useState(false);

  const safe = useMemo(
    () =>
      data
        .filter((d) => d.name)
        .map((d, i) => ({
          ...d,
          name: d.name || "—",
          color: d.color || FALLBACK_COLORS[i % FALLBACK_COLORS.length],
          countNum: Number(d.count) || 0,
          pctNum: Number(d.pct) || 0,
        })),
    [data]
  );

  const total = safe.reduce((acc, d) => acc + d.countNum, 0);
  const coverage =
    totalClosed > 0 ? +((total / totalClosed) * 100).toFixed(1) : 0;
  const maxCount = safe.reduce((m, d) => Math.max(m, d.countNum), 0);

  const visible = expanded ? safe : safe.slice(0, 6);
  const remaining = safe.length - visible.length;

  const coverageColor =
    coverage >= 80 ? "success" : coverage >= 50 ? "warning" : "error";

  return (
    <Card
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardHeader
        title="Causas de finalização"
        titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
        subheader={
          totalClosed > 0
            ? `${formatNumber(totalClosed)} fechados no período`
            : "Sem tickets fechados"
        }
        subheaderTypographyProps={{ variant: "caption" }}
        action={
          !loading && totalClosed > 0 ? (
            <Tooltip title="Percentual dos tickets fechados que receberam causa atribuída">
              <Chip
                size="small"
                color={coverageColor}
                variant="outlined"
                label={`${coverage}% classificados`}
                sx={{
                  height: 24,
                  fontSize: 11,
                  fontWeight: 600,
                  mr: 1,
                  mt: 0.5,
                  cursor: "help",
                }}
              />
            </Tooltip>
          ) : null
        }
      />

      <CardContent sx={{ pt: 0, pb: "16px !important", flex: 1 }}>
        {loading ? (
          <Stack spacing={1.75}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Box key={i}>
                <Skeleton variant="text" width="60%" height={16} />
                <Skeleton variant="rectangular" height={6} sx={{ borderRadius: 1, mt: 0.5 }} />
              </Box>
            ))}
          </Stack>
        ) : safe.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 5, color: "text.secondary" }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Nenhuma causa registrada
            </Typography>
            <Typography variant="caption">
              Atendentes ainda não classificaram tickets fechados no período.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {visible.map((d, i) => {
              const barWidth =
                maxCount > 0 ? Math.max(2, (d.countNum / maxCount) * 100) : 0;
              return (
                <Box key={d.id || d.name}>
                  <Stack
                    direction="row"
                    alignItems="baseline"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mb: 0.5 }}
                  >
                    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                          fontSize: 11,
                          fontWeight: 600,
                          color: "text.secondary",
                          minWidth: 18,
                        }}
                      >
                        {i + 1}.
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                      >
                        {d.name}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ flexShrink: 0 }}>
                      <Typography
                        sx={{
                          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                          fontWeight: 600,
                          fontSize: 13,
                        }}
                      >
                        {formatNumber(d.countNum)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: 'ui-monospace, monospace', minWidth: 42, textAlign: "right" }}
                      >
                        {d.pctNum.toFixed(1)}%
                      </Typography>
                    </Stack>
                  </Stack>
                  <Box
                    sx={{
                      height: 6,
                      width: "100%",
                      bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${barWidth}%`,
                        bgcolor: d.color,
                        borderRadius: 1,
                        transition: "width 280ms ease",
                      }}
                    />
                  </Box>
                </Box>
              );
            })}

            {remaining > 0 && !expanded && (
              <Typography
                variant="caption"
                onClick={() => setExpanded(true)}
                sx={{
                  cursor: "pointer",
                  color: "text.secondary",
                  pt: 0.5,
                  fontWeight: 500,
                  "&:hover": { color: "primary.main" },
                }}
              >
                Ver mais {remaining} causa{remaining > 1 ? "s" : ""}
              </Typography>
            )}
            {expanded && safe.length > 6 && (
              <Typography
                variant="caption"
                onClick={() => setExpanded(false)}
                sx={{
                  cursor: "pointer",
                  color: "text.secondary",
                  pt: 0.5,
                  fontWeight: 500,
                  "&:hover": { color: "primary.main" },
                }}
              >
                Mostrar menos
              </Typography>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default React.memo(CausesWidget);
